import React, { useState, useEffect, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { api } from '../lib/api.js';
import { FileEntry } from '@kcdash/shared';
import { MonacoCodeEditor } from '../components/MonacoCodeEditor.js';
import { Modal } from '../components/Modal.js';
import {
  Folder,
  File,
  FileCode,
  FileText,
  Upload,
  Plus,
  Trash2,
  Archive,
  Download,
  Edit,
  RotateCw,
  FolderPlus,
  ChevronRight,
  Home,
  CheckSquare,
  Square,
  Package,
  Copy,
  FolderArchive,
  Check,
} from 'lucide-react';

export const FileManagerPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const [currentPath, setCurrentPath] = useState('/');
  const [files, setFiles] = useState<FileEntry[]>([]);
  const [selectedPaths, setSelectedPaths] = useState<Set<string>>(new Set());
  const [isLoading, setIsLoading] = useState(true);

  // Editor Modal
  const [editingFile, setEditingFile] = useState<{ path: string; name: string; content: string } | null>(null);
  const [saveSuccessMsg, setSaveSuccessMsg] = useState('');

  // New File/Folder Modal
  const [isNewEntryOpen, setIsNewEntryOpen] = useState(false);
  const [newEntryName, setNewEntryName] = useState('');
  const [isCreatingDirectory, setIsCreatingDirectory] = useState(false);

  // Rename Modal
  const [renamingItem, setRenamingItem] = useState<{ oldPath: string; oldName: string } | null>(null);
  const [renameNewName, setRenameNewName] = useState('');

  // Copy Modal
  const [copyingItem, setCopyingItem] = useState<{ srcPath: string; name: string } | null>(null);
  const [copyDestName, setCopyDestName] = useState('');

  // Drag & drop state
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const loadFiles = async (dirPath: string = currentPath) => {
    if (!id) return;
    setIsLoading(true);
    setSelectedPaths(new Set());
    try {
      const data = await api.listFiles(id, dirPath);
      setFiles(data);
      setCurrentPath(dirPath);
    } catch (err: any) {
      alert(`Failed to load directory: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const initialPath = searchParams.get('path') || '/';
    const openFile = searchParams.get('open');
    loadFiles(initialPath);

    if (openFile && id) {
      const cleanPath = openFile.startsWith('/') ? openFile : `/${openFile}`;
      const cleanName = cleanPath.split('/').pop() || cleanPath;
      api.readFile(id, cleanPath).then(({ content }) => {
        setEditingFile({ path: cleanPath, name: cleanName, content });
      }).catch((err) => {
        console.warn('Could not auto-open file:', err);
      });
    }
  }, [id, searchParams]);

  // Navigate into directory
  const handleNavigate = (entry: FileEntry) => {
    if (entry.isDirectory) {
      loadFiles(entry.path);
    } else if (entry.isEditableText) {
      openFileInEditor(entry.path, entry.name);
    }
  };

  // Open file in Monaco
  const openFileInEditor = async (filePath: string, fileName: string) => {
    if (!id) return;
    try {
      const { content } = await api.readFile(id, filePath);
      setEditingFile({ path: filePath, name: fileName, content });
      setSaveSuccessMsg('');
    } catch (err: any) {
      alert(`Could not open file: ${err.message}`);
    }
  };

  // Save edited file (with server.toml safety backup handled automatically on backend)
  const handleSaveFile = async (newContent: string) => {
    if (!id || !editingFile) return;
    try {
      await api.writeFile(id, editingFile.path, newContent);
      setEditingFile((prev) => (prev ? { ...prev, content: newContent } : null));
      setSaveSuccessMsg('File saved successfully! (Backup created if server.toml)');
      setTimeout(() => setSaveSuccessMsg(''), 4000);
      loadFiles(currentPath);
    } catch (err: any) {
      alert(`Failed to save file: ${err.message}`);
    }
  };

  // Create file or directory
  const handleCreateEntry = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id || !newEntryName.trim()) return;

    const targetPath = `${currentPath.replace(/\/+$/, '')}/${newEntryName.trim()}`;
    try {
      await api.createEntry(id, targetPath, isCreatingDirectory);
      setIsNewEntryOpen(false);
      setNewEntryName('');
      loadFiles(currentPath);
    } catch (err: any) {
      alert(`Failed to create entry: ${err.message}`);
    }
  };

  // Rename
  const handleRename = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id || !renamingItem || !renameNewName.trim()) return;

    const parentDir = currentPath.replace(/\/+$/, '');
    const newPath = `${parentDir}/${renameNewName.trim()}`;

    try {
      await api.renameEntry(id, renamingItem.oldPath, newPath);
      setRenamingItem(null);
      loadFiles(currentPath);
    } catch (err: any) {
      alert(`Failed to rename: ${err.message}`);
    }
  };

  // Copy
  const handleCopy = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id || !copyingItem || !copyDestName.trim()) return;

    const parentDir = currentPath.replace(/\/+$/, '');
    const newPath = `${parentDir}/${copyDestName.trim()}`;

    try {
      await api.copyEntry(id, copyingItem.srcPath, newPath);
      setCopyingItem(null);
      loadFiles(currentPath);
    } catch (err: any) {
      alert(`Failed to copy: ${err.message}`);
    }
  };

  // Delete single file or folder
  const handleDeleteSingle = async (filePath: string, fileName: string) => {
    if (!id) return;
    if (!confirm(`Are you sure you want to delete '${fileName}'?`)) return;

    try {
      await api.deleteFiles(id, [filePath]);
      loadFiles(currentPath);
    } catch (err: any) {
      alert(`Delete failed: ${err.message}`);
    }
  };

  // Delete selected
  const handleDeleteSelected = async () => {
    if (!id || selectedPaths.size === 0) return;
    if (!confirm(`Are you sure you want to delete ${selectedPaths.size} selected item(s)?`)) return;

    try {
      await api.deleteFiles(id, Array.from(selectedPaths));
      loadFiles(currentPath);
    } catch (err: any) {
      alert(`Delete failed: ${err.message}`);
    }
  };

  // Zip selected
  const handleZipSelected = async () => {
    if (!id || selectedPaths.size === 0) return;
    const defaultZipName = `archive_${Date.now()}.zip`;
    const zipName = prompt('Enter target ZIP file name:', defaultZipName);
    if (!zipName) return;

    const targetZipPath = `${currentPath.replace(/\/+$/, '')}/${zipName.endsWith('.zip') ? zipName : zipName + '.zip'}`;

    try {
      await api.zipFiles(id, targetZipPath, Array.from(selectedPaths));
      loadFiles(currentPath);
    } catch (err: any) {
      alert(`Zip compression failed: ${err.message}`);
    }
  };

  // Unzip archive
  const handleUnzipSingle = async (filePath: string) => {
    if (!id) return;
    try {
      await api.unzipFile(id, filePath, currentPath);
      alert('Archive unzipped successfully');
      loadFiles(currentPath);
    } catch (err: any) {
      alert(`Unzip failed: ${err.message}`);
    }
  };

  // Handle uploaded file
  const handleFileUpload = async (filesToUpload: FileList | null) => {
    if (!id || !filesToUpload || filesToUpload.length === 0) return;

    setIsLoading(true);
    try {
      for (let i = 0; i < filesToUpload.length; i++) {
        await api.uploadFile(id, currentPath, filesToUpload[i]);
      }
      loadFiles(currentPath);
    } catch (err: any) {
      alert(`Upload failed: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const toggleSelectAll = () => {
    if (selectedPaths.size === files.length) {
      setSelectedPaths(new Set());
    } else {
      setSelectedPaths(new Set(files.map((f) => f.path)));
    }
  };

  const toggleSelectOne = (pathStr: string) => {
    const next = new Set(selectedPaths);
    if (next.has(pathStr)) {
      next.delete(pathStr);
    } else {
      next.add(pathStr);
    }
    setSelectedPaths(next);
  };

  // Format file size
  const formatSize = (bytes: number): string => {
    if (bytes === 0) return '-';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const pathParts = currentPath.split('/').filter(Boolean);

  return (
    <div className="space-y-4">
      {/* Hidden file input for uploads */}
      <input
        type="file"
        multiple
        ref={fileInputRef}
        onChange={(e) => handleFileUpload(e.target.files)}
        className="hidden"
      />

      {/* Breadcrumb Bar & Toolbar */}
      <div className="bg-panel-card border border-panel-border rounded-xl p-4 shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-4">
        {/* Breadcrumb Path */}
        <div className="flex items-center gap-1.5 text-xs font-mono text-neutral-300 overflow-x-auto py-1">
          <button
            onClick={() => loadFiles('/')}
            className="flex items-center gap-1 px-2 py-1 rounded hover:bg-neutral-800 text-neutral-400 hover:text-neutral-200 transition"
          >
            <Home className="w-3.5 h-3.5" />
            <span>root</span>
          </button>

          {pathParts.map((part, index) => {
            const partPath = '/' + pathParts.slice(0, index + 1).join('/');
            const isLast = index === pathParts.length - 1;

            return (
              <React.Fragment key={partPath}>
                <ChevronRight className="w-3.5 h-3.5 text-neutral-600 shrink-0" />
                <button
                  onClick={() => loadFiles(partPath)}
                  className={`px-2 py-1 rounded transition truncate max-w-[150px] ${
                    isLast ? 'text-brand-400 font-semibold' : 'text-neutral-400 hover:bg-neutral-800'
                  }`}
                >
                  {part}
                </button>
              </React.Fragment>
            );
          })}
        </div>

        {/* Action Buttons */}
        <div className="flex items-center gap-2">
          {selectedPaths.size > 0 && (
            <>
              <button
                onClick={handleZipSelected}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-panel-subtle hover:bg-neutral-800 border border-panel-border text-neutral-200 transition"
              >
                <Archive className="w-3.5 h-3.5 text-brand-400" />
                <span>Zip ({selectedPaths.size})</span>
              </button>

              <button
                onClick={handleDeleteSelected}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-red-950/70 hover:bg-red-900 border border-red-800/80 text-red-300 transition"
              >
                <Trash2 className="w-3.5 h-3.5" />
                <span>Delete ({selectedPaths.size})</span>
              </button>
            </>
          )}

          <button
            onClick={() => fileInputRef.current?.click()}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-panel-subtle hover:bg-neutral-800 border border-panel-border text-neutral-200 transition"
          >
            <Upload className="w-3.5 h-3.5 text-brand-400" />
            <span>Upload</span>
          </button>

          <button
            onClick={() => {
              setIsCreatingDirectory(false);
              setIsNewEntryOpen(true);
            }}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-panel-subtle hover:bg-neutral-800 border border-panel-border text-neutral-200 transition"
          >
            <Plus className="w-3.5 h-3.5" />
            <span>New File</span>
          </button>

          <button
            onClick={() => {
              setIsCreatingDirectory(true);
              setIsNewEntryOpen(true);
            }}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-panel-subtle hover:bg-neutral-800 border border-panel-border text-neutral-200 transition"
          >
            <FolderPlus className="w-3.5 h-3.5" />
            <span>New Folder</span>
          </button>

          <button
            onClick={() => loadFiles(currentPath)}
            className="p-1.5 rounded-lg bg-panel-subtle hover:bg-neutral-800 border border-panel-border text-neutral-400 hover:text-neutral-200 transition"
            title="Refresh Directory"
          >
            <RotateCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Drag & Drop File Listing */}
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setIsDragOver(true);
        }}
        onDragLeave={() => setIsDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setIsDragOver(false);
          handleFileUpload(e.dataTransfer.files);
        }}
        className={`bg-panel-card border rounded-xl overflow-hidden shadow-sm transition ${
          isDragOver ? 'border-brand-500 bg-brand-950/20' : 'border-panel-border'
        }`}
      >
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs text-neutral-300">
            <thead className="bg-panel-subtle border-b border-panel-border text-neutral-400 font-mono text-[11px] uppercase">
              <tr>
                <th className="w-10 px-4 py-3">
                  <button onClick={toggleSelectAll} className="flex items-center text-neutral-400 hover:text-neutral-200">
                    {files.length > 0 && selectedPaths.size === files.length ? (
                      <CheckSquare className="w-4 h-4 text-brand-400" />
                    ) : (
                      <Square className="w-4 h-4" />
                    )}
                  </button>
                </th>
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3 w-28">Size</th>
                <th className="px-4 py-3 w-44">Modified</th>
                <th className="px-4 py-3 w-36 text-right">Actions</th>
              </tr>
            </thead>

            <tbody className="divide-y divide-panel-border/50">
              {isLoading ? (
                <tr>
                  <td colSpan={5} className="text-center py-12 text-neutral-500 font-mono">
                    Loading directory contents...
                  </td>
                </tr>
              ) : files.length === 0 ? (
                <tr>
                  <td colSpan={5} className="text-center py-12 text-neutral-500 font-mono">
                    Directory is empty. Drag and drop files here to upload.
                  </td>
                </tr>
              ) : (
                files.map((file) => {
                  const isSelected = selectedPaths.has(file.path);

                  return (
                    <tr
                      key={file.path}
                      className={`hover:bg-panel-subtle/50 transition ${
                        isSelected ? 'bg-brand-950/30' : ''
                      }`}
                    >
                      <td className="px-4 py-2.5">
                        <button
                          onClick={() => toggleSelectOne(file.path)}
                          className="flex items-center text-neutral-400 hover:text-neutral-200"
                        >
                          {isSelected ? (
                            <CheckSquare className="w-4 h-4 text-brand-400" />
                          ) : (
                            <Square className="w-4 h-4" />
                          )}
                        </button>
                      </td>

                      <td
                        onClick={() => handleNavigate(file)}
                        className="px-4 py-2.5 font-mono flex items-center gap-2 cursor-pointer select-none"
                      >
                        {file.isDirectory ? (
                          <Folder className="w-4 h-4 text-brand-400 shrink-0" />
                        ) : file.isEditableText ? (
                          <FileCode className="w-4 h-4 text-sky-400 shrink-0" />
                        ) : file.extension === '.zip' ? (
                          <Package className="w-4 h-4 text-purple-400 shrink-0" />
                        ) : (
                          <File className="w-4 h-4 text-neutral-400 shrink-0" />
                        )}

                        <span className="font-medium hover:text-brand-300 transition truncate max-w-md">
                          {file.name}
                        </span>

                        {file.name === 'server.toml' && (
                          <span className="text-[10px] text-amber-400 bg-amber-950 px-1.5 py-0.5 rounded border border-amber-800">
                            CONFIG
                          </span>
                        )}

                        {file.isEditableText && file.name !== 'server.toml' && (
                          <span className="text-[10px] text-brand-500 bg-brand-950 px-1.5 py-0.5 rounded border border-brand-900">
                            EDITABLE
                          </span>
                        )}
                      </td>

                      <td className="px-4 py-2.5 text-neutral-500 font-mono">{formatSize(file.sizeBytes)}</td>

                      <td className="px-4 py-2.5 text-neutral-500 font-mono">
                        {new Date(file.modifiedAt).toLocaleString()}
                      </td>

                      <td className="px-4 py-2.5 text-right">
                        <div className="flex items-center justify-end gap-1">
                          {file.isEditableText && (
                            <button
                              onClick={() => openFileInEditor(file.path, file.name)}
                              title="Edit in Monaco"
                              className="p-1.5 rounded hover:bg-neutral-800 text-neutral-400 hover:text-brand-300 transition"
                            >
                              <Edit className="w-3.5 h-3.5" />
                            </button>
                          )}

                          {file.extension === '.zip' && (
                            <button
                              onClick={() => handleUnzipSingle(file.path)}
                              title="Unzip Archive"
                              className="p-1.5 rounded hover:bg-neutral-800 text-purple-400 hover:text-purple-300 transition"
                            >
                              <FolderArchive className="w-3.5 h-3.5" />
                            </button>
                          )}

                          {!file.isDirectory && (
                            <a
                              href={`/api/v1/servers/${id}/files/download?path=${encodeURIComponent(file.path)}`}
                              title="Download"
                              className="p-1.5 rounded hover:bg-neutral-800 text-neutral-400 hover:text-neutral-200 transition"
                            >
                              <Download className="w-3.5 h-3.5" />
                            </a>
                          )}

                          <button
                            onClick={() => {
                              setCopyingItem({ srcPath: file.path, name: file.name });
                              setCopyDestName(`${file.name}.copy`);
                            }}
                            title="Copy"
                            className="p-1.5 rounded hover:bg-neutral-800 text-neutral-400 hover:text-neutral-200 transition"
                          >
                            <Copy className="w-3.5 h-3.5" />
                          </button>

                          <button
                            onClick={() => {
                              setRenamingItem({ oldPath: file.path, oldName: file.name });
                              setRenameNewName(file.name);
                            }}
                            title="Rename"
                            className="p-1.5 rounded hover:bg-neutral-800 text-neutral-400 hover:text-neutral-200 transition"
                          >
                            <Edit className="w-3.5 h-3.5" />
                          </button>

                          <button
                            onClick={() => handleDeleteSingle(file.path, file.name)}
                            title="Delete"
                            className="p-1.5 rounded hover:bg-neutral-800 text-red-400 hover:text-red-300 transition"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Monaco Editor Modal */}
      {editingFile && (
        <Modal
          isOpen={true}
          onClose={() => setEditingFile(null)}
          title={`Editing: ${editingFile.name}`}
          maxWidth="2xl"
        >
          <div className="space-y-3">
            {saveSuccessMsg && (
              <div className="p-2.5 rounded-lg bg-emerald-950/80 border border-emerald-800 text-emerald-300 text-xs font-mono flex items-center gap-2">
                <Check className="w-4 h-4 text-emerald-400 shrink-0" />
                <span>{saveSuccessMsg}</span>
              </div>
            )}
            <MonacoCodeEditor
              filename={editingFile.name}
              value={editingFile.content}
              onSave={handleSaveFile}
            />
          </div>
        </Modal>
      )}

      {/* New File / Folder Modal */}
      <Modal
        isOpen={isNewEntryOpen}
        onClose={() => setIsNewEntryOpen(false)}
        title={isCreatingDirectory ? 'Create New Folder' : 'Create New File'}
        maxWidth="sm"
      >
        <form onSubmit={handleCreateEntry} className="space-y-4">
          <div>
            <label className="block text-xs font-semibold text-neutral-300 uppercase mb-1">
              {isCreatingDirectory ? 'Folder Name' : 'File Name (e.g. freeroam.lua)'}
            </label>
            <input
              type="text"
              required
              autoFocus
              value={newEntryName}
              onChange={(e) => setNewEntryName(e.target.value)}
              className="w-full px-3 py-2 rounded-lg bg-panel-input border border-panel-border text-sm text-neutral-200 focus:outline-none focus:border-brand-500 font-mono"
            />
          </div>

          <div className="flex items-center justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={() => setIsNewEntryOpen(false)}
              className="px-3 py-1.5 text-xs text-neutral-400 hover:text-neutral-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-1.5 rounded-lg text-xs font-medium bg-brand-600 hover:bg-brand-500 text-white"
            >
              Create
            </button>
          </div>
        </form>
      </Modal>

      {/* Rename Modal */}
      {renamingItem && (
        <Modal
          isOpen={true}
          onClose={() => setRenamingItem(null)}
          title={`Rename '${renamingItem.oldName}'`}
          maxWidth="sm"
        >
          <form onSubmit={handleRename} className="space-y-4">
            <div>
              <label className="block text-xs font-semibold text-neutral-300 uppercase mb-1">
                New Name
              </label>
              <input
                type="text"
                required
                autoFocus
                value={renameNewName}
                onChange={(e) => setRenameNewName(e.target.value)}
                className="w-full px-3 py-2 rounded-lg bg-panel-input border border-panel-border text-sm text-neutral-200 focus:outline-none focus:border-brand-500 font-mono"
              />
            </div>

            <div className="flex items-center justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => setRenamingItem(null)}
                className="px-3 py-1.5 text-xs text-neutral-400 hover:text-neutral-200"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-1.5 rounded-lg text-xs font-medium bg-brand-600 hover:bg-brand-500 text-white"
              >
                Rename
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* Copy Modal */}
      {copyingItem && (
        <Modal
          isOpen={true}
          onClose={() => setCopyingItem(null)}
          title={`Copy '${copyingItem.name}'`}
          maxWidth="sm"
        >
          <form onSubmit={handleCopy} className="space-y-4">
            <div>
              <label className="block text-xs font-semibold text-neutral-300 uppercase mb-1">
                Destination Name
              </label>
              <input
                type="text"
                required
                autoFocus
                value={copyDestName}
                onChange={(e) => setCopyDestName(e.target.value)}
                className="w-full px-3 py-2 rounded-lg bg-panel-input border border-panel-border text-sm text-neutral-200 focus:outline-none focus:border-brand-500 font-mono"
              />
            </div>

            <div className="flex items-center justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => setCopyingItem(null)}
                className="px-3 py-1.5 text-xs text-neutral-400 hover:text-neutral-200"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-1.5 rounded-lg text-xs font-medium bg-brand-600 hover:bg-brand-500 text-white"
              >
                Copy
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
};
